import React, { useMemo, useState } from 'react';

import { Button, CollapsibleBlock, Modal, Tooltip } from '@components/common';
import TextAvatar from '@components/common/TextAvatar';
import AcquireAssetForm from '@components/ModalComponents/AcquireAssetForm';
import FileTypeSelectionForm from '@components/ModalComponents/FileTypeSelectionForm';
import QueryAssetForm from '@components/ModalComponents/QueryAssetForm';

import ExportIcon from '@icons/ExportIcon';

import { useMutateExportAssetsFile } from '@hooks/assets/useMutateExportAssetsFile';
import { useMutateExportPolygonAssets } from '@hooks/assets/useMutateExportPolygonAssets';

import { auth, map } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { isEmpty } from '@utils/common';
import { getAssetTypesDtoForFilter } from '@utils/getAssetTypesDtoForFilter';
import { calculateAssetsByOwners, calculateAssetsByType, IAssetByType } from './helpers/normalizeAssetsData';

import { IExportDefaultDTO } from '@interfaces/common/defaultDTO';
import { ITotalByAssetType } from '@interfaces/map/map';
import { IMultipleAssetsInfoProps } from './interfaces';

import { exportAssetsRadioButtonsData } from '@constants/common';
import { MODAL_WIDTH_455, MODAL_WIDTH_580 } from '@constants/modals';
import { noSubTypeOptions } from '@components/routes/AdminPanel/AssetsList/constants';

import styles from './SelectedAssetInfo.module.scss';

const collapsibleBlocksInitialState = {
	assetTypes: false,
	owners: false,
	rentInfo: false,
};

const MultipleAssetsInfo = ({
	areBoroughsEnabled,
	areActiveBoroughs,
	areActivePolygons,
	assets,
	isLoading,
	polygonsSummary,
}: IMultipleAssetsInfoProps) => {
	const [
		collapsibleBlocks,
		setCollapsibleBlocks,
	] = useState<Record<string, boolean>>(() => ({
		...collapsibleBlocksInitialState,
		assetTypes: true,
	}));

	const { accessPermission } = useAppSelector(auth);
	const {
		activeCustomPolygons,
		activeBoroughs,
		appliedAssetsFilterValues,
		isAcquiredAssetsShownApplied,
		appliedAcquiredAssetsTypeFilter,
	} = useAppSelector(map);

	const {
		networkOperatorAssetEnquirer,
		networkOperatorAssetViewer,
		networkOperatorOrganizationAdministrator,
		assetConsumerOrganizationAdministrator,
		assetConsumerAssetEnquirer,
		platformOwnerAdministrator,
		assetConsumerAssetViewer,
	} = accessPermission;

	const {
		isMyLinked,
		assetSiteAvailabilityStatus,
		assetType,
		detailed,
		isAvailable,
		ownerId,
		powerSupplyAvailabilityStatus,
		backhaulAccessibilityStatus,
	} = appliedAssetsFilterValues;

	const isDisabledExport = isLoading;

	const areAssetFormsAvailable = networkOperatorAssetEnquirer || assetConsumerAssetEnquirer;
	const isTotalPerOwnerAvailable = (platformOwnerAdministrator
		|| networkOperatorOrganizationAdministrator
		|| networkOperatorAssetViewer
		|| networkOperatorAssetEnquirer
		|| assetConsumerOrganizationAdministrator
		|| assetConsumerAssetEnquirer
		|| assetConsumerAssetViewer
	);

	const isPolygonsSummary = (!isEmpty(polygonsSummary) && areActiveBoroughs)
		|| (!isEmpty(polygonsSummary) && areActivePolygons);
	const isValidAssets = !isEmpty(assets);

	const totalAssetsCount = isPolygonsSummary ? polygonsSummary?.totalAssets : assets?.length;

	const totalByAssetOwner = isPolygonsSummary
		? polygonsSummary.totalByAssetOwner
		: isValidAssets && calculateAssetsByOwners(assets);

	const [isQueryAssetForm, setIsQueryAssetForm] = useState(false);
	const [isAcquireAssetForm, setIsAcquireAssetForm] = useState(false);
	const [isQueryFormDirty, setIsQueryFormDirty] = useState(false);
	const [isAcquireFormDirty, setIsAcquireFormDirty] = useState(false);
	const [isOpenFileTypeSelectionModal, setIsOpenFileTypeSelectionModal] = useState(false);

	const toggleCollapsibleBlock = (name: string) => {
		const isAlreadyOpen = collapsibleBlocks[name];

		if (isAlreadyOpen) setCollapsibleBlocks(collapsibleBlocksInitialState);
		else {
			setCollapsibleBlocks(prevState => {
				const newState: Record<string, boolean> = {};

				for (const key in prevState) {
					newState[key] = key === name;
				}

				return newState;
			});
		}
	};

	const toggleQueryAssetForm = () => setIsQueryAssetForm(!isQueryAssetForm);
	const toggleAcquireAssetForm = () => setIsAcquireAssetForm(!isAcquireAssetForm);
	const toggleFileTypeSelectionModal = () => setIsOpenFileTypeSelectionModal(prevState => !prevState);

	const closeModals = () => setIsOpenFileTypeSelectionModal(false);

	const { handleExportPolygonAssets, isLoadingExportPolygonAssets } = useMutateExportPolygonAssets();
	const { handlePreparingExport: handleExportSelectedAssets } = useMutateExportAssetsFile();

	const handleExport = () => {
		if (isDisabledExport) return;

		toggleFileTypeSelectionModal();
	};

	const assetTypeWithSubTypes = assetType ? getAssetTypesDtoForFilter(assetType) : undefined;

	const handlePreparingExport = ({ fileType }: IExportDefaultDTO<undefined>) => {
		if (activeCustomPolygons.length || activeBoroughs.length) {
			const dtoForPolygons = {
				polygonIds: activeCustomPolygons,
				assetState: {
					isLinkedWithSite: isMyLinked ? isMyLinked : undefined,
					isOrganisationRent: isAcquiredAssetsShownApplied ? isAcquiredAssetsShownApplied : undefined,
					timeRentType: isAcquiredAssetsShownApplied ? appliedAcquiredAssetsTypeFilter : undefined,
				},
				boroughIds: areBoroughsEnabled ? activeBoroughs : undefined,
				detailed: detailed
					? {
						height: detailed.height,
						withoutHeight: detailed.withoutHeight,
						siteAvailabilityReviewDate: detailed.siteAvailabilityReviewDate,
						pricing: detailed.indicativeAcceptablePricing,
					}
					: undefined,
				filter: {
					assetOwnerIds: ownerId,
					assetSiteASs: assetSiteAvailabilityStatus,
					assetTypeWithSubTypes,
					backhaulASs: backhaulAccessibilityStatus,
					powerSupplyASs: powerSupplyAvailabilityStatus,
					isAvailable: isAvailable,
				},
				fileType,
			};

			handleExportPolygonAssets(dtoForPolygons);
		}

		if (!activeCustomPolygons.length && !activeBoroughs.length) {
			handleExportSelectedAssets({
				fileType,
				ids: assets.map(({ id }) => id),
			});
		}
	};

	const calculatedAssetsTypes = useMemo(() => {
		return calculateAssetsByType(assets);
	}, [assets]);

	return (
		<div className={styles.container}>
			<div className={styles.assetType}>
				<div className={styles.type}>
					Selected
				</div>
				{polygonsSummary && (!!activeCustomPolygons?.length || !!activeBoroughs?.length) && (
					<div className={styles.polygonNames}>
						{polygonsSummary.names.join(', ')}
					</div>
				)}
			</div>
			<div className={styles.baseInfo}>
				<div className={styles.info}>
					<div className={styles.ownerInfo}>
						<div>
							<span>Total Assets: </span>
							{totalAssetsCount}
						</div>
						<Tooltip content={<span>Export Assets to file</span>}>
							<div onClick={handleExport}>
								<ExportIcon isDisabled={isDisabledExport} />
							</div>
						</Tooltip>
					</div>
				</div>
			</div>
			{areAssetFormsAvailable && (
				<div className={styles.actions}>
					<Button
						disabled={isLoading}
						onClick={toggleQueryAssetForm}
						text='Query assets'
					/>
					<Button
						disabled={isLoading}
						onClick={toggleAcquireAssetForm}
						text='Acquire assets'
					/>
				</div>
			)}
			<CollapsibleBlock
				isOpen={collapsibleBlocks.assetTypes}
				label={<div className={styles.collapsibleLabel}>Totals by Asset type</div>}
				name='assetTypes'
				toggleState={toggleCollapsibleBlock}
				withSeparatorLine
			>
				<div className={styles.totalsByAsset}>
					{isPolygonsSummary
						? polygonsSummary?.totalByAssetType?.map((item: ITotalByAssetType) => {
							const isNoSubtypeOnly = item.totalByAssetType.length === 1
								&& item.totalByAssetType[0].type.assetSubTypeName === noSubTypeOptions.label;

							return (
								<div
									key={`${item.type.id}`}
									className={styles.totalsByAssetItem}
								>
									<TextAvatar
										avatarId={item.type.icon.id}
										className={styles.assetIcon}
										fontSize='12px'
										isProfileLogo={false}
										size='24px'
									/>
									<span>{item.type.assetTypeName}:</span>
									{item.count}
									<div className={styles.subTypes}>
										{!isNoSubtypeOnly && item.totalByAssetType
											.map((subtype) => {
												return (
													<div key={`${subtype.type.assetSubTypeName}`}>
														<span>{subtype.type.assetSubTypeName}: </span>
														{subtype.count}
													</div>
												);
											})}
									</div>
								</div>
							);
						})
						: calculatedAssetsTypes.map((item: IAssetByType) => {
							const isNoSubtypeOnly = item?.subTypes.length === 1
								&& item?.subTypes[0]?.name === noSubTypeOptions.label;

							return (
								<div
									key={`${item.name}`}
									className={styles.totalsByAssetItem}
								>
									<TextAvatar
										avatarId={item.iconId}
										className={styles.assetIcon}
										fontSize='12px'
										isProfileLogo={false}
										size='24px'
									/>
									<span>{item.name}: </span>
									{item.count}
									<div className={styles.subTypes}>
										{!isNoSubtypeOnly && (
											item.subTypes
												.filter((subtype) => subtype.count > 0)
												.map((subtype) => {
													return (
														<div key={`${subtype.name}`}>
															<span>{subtype.name}: </span>
															{subtype.count}
														</div>
													);
												})
										)}
									</div>
								</div>
							);
						})
					}
				</div>
			</CollapsibleBlock>
			{isTotalPerOwnerAvailable && (
				<CollapsibleBlock
					isOpen={collapsibleBlocks.owners}
					label={<div className={styles.collapsibleLabel}>Total per Owner</div>}
					name='owners'
					toggleState={toggleCollapsibleBlock}
					withSeparatorLine
				>
					<div className={styles.totalsByOwners}>
						{Object.values(totalByAssetOwner || {}).map(({ name, count }) => {
							return (
								<div key={`${name}-${count}`}>
									<span>{name}: </span>
									{count}
								</div>);
						})}
					</div>
				</CollapsibleBlock>
			)}

			<Modal
				isConfirmDialogNeededOnClose
				isDirtyForm={isQueryFormDirty}
				isOpen={isQueryAssetForm}
				onClose={toggleQueryAssetForm}
				title='Query assets'
				width={MODAL_WIDTH_580}
			>
				<QueryAssetForm
					closeModal={() => setIsQueryAssetForm(false)}
					setIsDirtyForm={setIsQueryFormDirty}
				/>
			</Modal>

			<Modal
				isConfirmDialogNeededOnClose
				isDirtyForm={isAcquireFormDirty}
				isOpen={isAcquireAssetForm}
				onClose={toggleAcquireAssetForm}
				title='Acquire assets'
				width={MODAL_WIDTH_580}
			>
				<AcquireAssetForm
					closeModal={() => setIsAcquireAssetForm(false)}
					setIsDirtyForm={setIsAcquireFormDirty}
				/>
			</Modal>

			<Modal
				isOpen={isOpenFileTypeSelectionModal}
				onClose={toggleFileTypeSelectionModal}
				title='File extension to export'
				width={MODAL_WIDTH_455}
			>
				<FileTypeSelectionForm
					closeModals={closeModals}
					exportRadioButtonsData={exportAssetsRadioButtonsData}
					handlePreparingExport={handlePreparingExport}
					isLoading={isLoadingExportPolygonAssets}
					toggleModal={toggleFileTypeSelectionModal}
				/>
			</Modal>
		</div>
	);
};

export default MultipleAssetsInfo;
